import { CognitoUser } from '@aws-amplify/auth';
import { FederatedSignInOptions, SignInOpts } from '@aws-amplify/auth/lib-esm/types';
import { Hub } from '@aws-amplify/core';
import Config from 'config';
import { t } from 'i18n';
import _ from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import navigate from 'models/router-gate';
import { storage } from 'models/store-global';

import AlertsService from 'services/AlertsService';
import LoginAsUserService from 'services/LoginAsUserService';
import ModalsService from 'services/ModalsService';

import { MyStore } from 'types/Store';
import { type CurrentParticipant } from 'types/zod/current-participant';

export const UI_AUTH_CHANNEL = 'UI Auth';
export const AUTH_STATE_CHANGE_EVENT = 'AuthStateChange';
export const PRELIMINARY = '_preliminary';

const AuthService = {
    logout: async () => {
        try {
            LoginAsUserService.beforeLogout();
            localStorage.removeItem('gift-trust');
            await $$auth.signOutFx();
        } catch (error) {
            AlertsService.handleError(error);
        }
    },

    changeAuthState: (nextAuthState: string, data?: any) => {
        Hub.dispatch(UI_AUTH_CHANNEL, {
            event: AUTH_STATE_CHANGE_EVENT,
            message: nextAuthState,
            data,
        });
    },

    complexSignIn: async (user: SignInOpts): Promise<CognitoUser | undefined> => {
        try {
            return await $$auth.signInFx(user);
        } catch (error: unknown) {
            console.log({ error });
            if (error && typeof error === 'object' && 'code' in error && error.code === 'UserNotConfirmedException') {
                storage.set(
                    (store): MyStore => ({ ...store, authData: { ...(store.authData || {}), credentials: user } }),
                );
                await $$auth.resendSignUpFx({ username: user.username });
                navigate('/verification');
            } else {
                AlertsService.handleError(error);
            }
            storage.set((store): MyStore => ({ ...store, authData: { ...(store.authData || {}), validated: false } }));
            return undefined;
        }
    },

    signInWith: async (provider: string) => {
        try {
            return await $$auth.federatedSignInFx({ provider } as FederatedSignInOptions);
        } catch (error) {
            AlertsService.handleError(error);
        }
    },

    signUp: async (user: any) => {
        try {
            const response = await $$auth.signUpFx(user);
            storage.set(
                (store): MyStore => ({
                    ...store,
                    authData: {
                        ...(store.authData || {}),
                        credentials: user,
                    },
                }),
            );
            const isTempUser = user?.attributes?.given_name === `temp`;
            if (!isTempUser) {
                AlertsService.showSuccess(t('signUp.success'));
            }
            return response;
        } catch (error) {
            AlertsService.handleError(error);
        }
    },

    forgotPassword: async (email: string, role?: string) => {
        try {
            await $$auth.forgotPasswordFx({ username: email });
            navigate(role ? `/?role=${role}` : '/');
            ModalsService.showModality(
                t('forgotPassword.afterResetPasswordModal.header'),
                t('forgotPassword.afterResetPasswordModal.description'),
                { confirmButton: t('forgotPassword.afterResetPasswordModal.confirmButton'), displayCloseButton: false },
            );
        } catch (error: any) {
            if (
                error.code === 'UserNotConfirmedException' ||
                (error?.message && error?.message.indexOf('no registered/verified email') > 0)
            ) {
                await $$auth.resendSignUpFx({ username: email });
                navigate(`/verification`);
            } else {
                AlertsService.handleError(error);
            }
        }
    },

    forgotPasswordSubmit: async (username: any, code: any, password: any) => {
        try {
            await $$auth.forgotPasswordSubmitFx({ username, code, password });
            AlertsService.showSuccess(t('forgotPassword.password_changed'));
            return true;
        } catch (error) {
            AlertsService.handleError(error);
            return false;
        }
    },

    respondToAuthChallenge: async (user: CognitoUser, password: string) => {
        try {
            await $$auth.completeNewPasswordFx({ user, password });
            return true;
        } catch (error) {
            AlertsService.handleError(error);
            return false;
        }
    },

    cleanCognitoCookies: () => {
        const cookieDomain = Config.application.cookieDomain;
        const cookies = document.cookie.split(';');
        cookies.forEach((cookie) => {
            const name = cookie.trim().split('=')[0];
            if (name.startsWith('CognitoIdentityServiceProvider' || name === 'refreshTokenDate')) {
                document.cookie = `${name}=1; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=${cookieDomain}; path=/`;
            }
        });

        for (const key in localStorage) {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
                localStorage.removeItem(key);
            }
        }
    },

    signUpAfterAssessment: async (signUpAttributes: any) => {
        const oldAwsId = $$auth.$currentParticipant.map((p) => p?.user.aws_id).getState();
        if (oldAwsId) {
            signUpAttributes.attributes['custom:migrate'] = oldAwsId;
        }

        await AuthService.signUp(signUpAttributes);
        await AuthService.logout();
    },

    getIsPreliminaryUser: (data: CurrentParticipant) => {
        const isPreliminaryUser =
            data.last_name === PRELIMINARY ||
            (data.phone_number === '+19999999999' && data.date_of_birth === '1970-01-01');
        return isPreliminaryUser;
    },

    removePreliminaryFields: (data: CurrentParticipant) => {
        const isPreliminaryUser = AuthService.getIsPreliminaryUser(data);
        if (isPreliminaryUser) {
            return _(_.cloneDeep(data))
                .set('last_name', data.last_name !== PRELIMINARY ? data.last_name : '')
                .set('phone_number', '')
                .set('address.domicile.state_code', data.domicile_state_code)
                .omit('date_of_birth')
                .value();
        } else {
            return data;
        }
    },

    changePassword: async (oldPassword: string, newPassword: string) => {
        try {
            const user = await $$auth.currentUserPoolUserFx();
            await $$auth.changePasswordFx({ user, oldPassword, newPassword });
            await $$api.users.sendPasswordChangedEmailFx();
            AlertsService.showSuccess(t('changePassword.success_message'));
        } catch (error: any) {
            if (error.code === 'NotAuthorizedException') {
                AlertsService.showError(t('errors:changePassword.incorrect_password'));
            } else {
                AlertsService.showError(error?.message);
            }
        }
    },
};

export default AuthService;
